import React from 'react';
import { Typography } from '@mui/material';
import LegalPageLayout from './LegalPageLayout';

const Disclaimer = () => {
  return (
    <LegalPageLayout
      title="Disclaimer"
      updated="October 13, 2026"
    >
      <Typography paragraph>
        HaggleFreight is a transportation marketplace operated by
        GoCourier Inc. ("HaggleFreight," "we," "us," or "our"). The
        HaggleFreight website, mobile applications, and related
        marketplace features are collectively referred to as the
        "Platform." Customers and shippers are referred to as
        "Customers." Independent drivers, owner-operators, carriers,
        and transportation companies offering services through the
        Platform are referred to as "Transportation Providers."
      </Typography>

      <Typography paragraph>
        This Disclaimer explains important limitations, risks, and
        responsibilities associated with using the Platform. It
        supplements the Terms of Service and applicable accepted
        agreements, including the Customer Agreement, Business
        Customer Agreement, Carrier Agreement, Refund Policy,
        Cancellation Policy, and Acceptable Use Policy. The Privacy
        Policy addresses our handling of personal information.
      </Typography>

      <Typography paragraph>
        This Disclaimer does not independently expand the liability
        exclusions in the Terms of Service, create an undisclosed
        charge, or remove a right that cannot lawfully be waived.
        Applicable mandatory law takes priority. If this Disclaimer
        conflicts with the Terms of Service, the Terms of Service
        control, subject to applicable law and the document-priority
        provisions in those Terms.
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. No Transportation Services
      </Typography>

      <Typography paragraph>
        GoCourier Inc. provides marketplace software rather than
        physically transporting the shipments offered by
        Transportation Providers. GoCourier Inc. does not itself
        drive the vehicles or take physical custody of cargo in
        those marketplace transactions. Physical transportation is
        performed by the relevant Transportation Provider.
      </Typography>

      <Typography paragraph>
        Available Platform tools allow users to discover
        opportunities, exchange information, submit bids, negotiate,
        communicate, and record their agreements. A listing,
        notification, payment record, or shipment-status update is
        not a representation that GoCourier Inc. is performing the
        transportation.
      </Typography>

      <Typography paragraph>
        This Disclaimer does not appoint GoCourier Inc. to accept
        shipments or sign transportation contracts on a user's
        behalf. Describing the Platform as a marketplace does not
        determine its regulatory classification or override any
        responsibility imposed by applicable law. Nothing in this
        Disclaimer authorizes GoCourier Inc. or a user to perform
        an activity requiring a license, permit, registration, or
        operating authority without satisfying the applicable
        requirements.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Independent Users
      </Typography>

      <Typography paragraph>
        Customers and Transportation Providers make their own
        decisions about counterparties, shipment terms, prices,
        and acceptance. Transportation Providers are responsible
        for performing the transportation services they agree to
        provide, including responsibilities for their vehicles,
        authorized personnel, equipment, and lawful operations.
      </Typography>

      <Typography paragraph>
        The intended independent marketplace relationship does not
        override a legal relationship or classification established
        by actual conduct and applicable law. A driver may be an
        employee or authorized representative of the performing
        carrier. Use of HaggleFreight does not itself determine that
        relationship.
      </Typography>

      <Typography paragraph>
        Profiles, ratings, reviews, uploaded documents, account
        approval, and verification indicators are not guarantees of
        identity, current operating authority, insurance coverage,
        creditworthiness, reliability, or suitability for every
        shipment. Any description of verification must accurately
        reflect the checks actually performed. Users should review
        what a particular check covers and independently confirm
        information important to their transaction.
      </Typography>

      <Typography paragraph>
        We do not guarantee another user's statements or future
        performance. This limitation does not excuse an inaccurate
        representation we make about our own checks or services,
        or eliminate an obligation imposed on us by law.
      </Typography>

      <Typography variant="h5" gutterBottom>
        3. No Guarantee of Service
      </Typography>

      <Typography paragraph>
        HaggleFreight does not guarantee that a shipment listing
        will receive a bid, that a particular Transportation
        Provider will be available, or that a transportation
        agreement will be formed. Access to the Platform does not
        guarantee a minimum number of shipments, customers,
        business relationships, or transportation opportunities.
      </Typography>

      <Typography paragraph>
        Shipment availability and performance may be affected by
        traffic, weather, road restrictions, equipment problems,
        facility access, user conduct, network conditions, and
        other circumstances. Estimated pickup times, arrival
        times, and completion dates are not guarantees unless an
        applicable agreement expressly provides otherwise.
      </Typography>

      <Typography paragraph>
        Platform access may be interrupted by maintenance,
        technical problems, network outages, security incidents,
        or other events. These limitations do not override an
        express service commitment we make, authorize retention
        of money we are legally required to return, or eliminate
        remedies available for failure to provide an agreed
        service.
      </Typography>

      <Typography variant="h5" gutterBottom>
        4. No Guarantee of Pricing or Earnings
      </Typography>

      <Typography paragraph>
        Displayed estimates, suggested rates, historical
        information, projected earnings, and pricing comparisons
        are informational. They do not guarantee a final
        transportation price, profit, savings, payment ability,
        or a particular commercial result.
      </Typography>

      <Typography paragraph>
        Customers and Transportation Providers are responsible
        for reviewing and accepting the applicable transportation
        price and material service terms. A suggested price is
        not a substitute for their agreement. Additional charges
        require an applicable contractual or legal basis and any
        required disclosure and authorization.
      </Typography>

      <Typography paragraph>
        This Disclaimer does not establish a subscription price,
        a billing start date, a fixed free-access period, or a
        permanent Platform-fee structure. Any future paid offer
        must be presented and accepted under the pricing and
        payment-authorization provisions of the Terms of Service.
        General acceptance of this Disclaimer does not authorize
        an unknown future charge or recurring billing.
      </Typography>

      <Typography paragraph>
        Free access to Platform features does not make
        transportation services free. Transportation charges,
        Platform charges, and third-party charges must be
        distinguished in the relevant transaction or purchase
        disclosures.
      </Typography>

      <Typography variant="h5" gutterBottom>
        5. AI and Platform Features
      </Typography>

      <Typography paragraph>
        Where available, the Platform may provide automated
        suggestions, search results, calculations, or AI-assisted
        features. Such outputs may be inaccurate, incomplete,
        outdated, or unsuitable for a particular situation.
        Mentioning a feature in this Disclaimer does not mean
        that the feature is currently available.
      </Typography>

      <Typography paragraph>
        Users should independently review information that
        materially affects a shipment, payment, or safety
        decision. Automated information does not establish a
        carrier's legal qualifications, guarantee insurance
        coverage, approve regulated cargo, or replace required
        transportation documents.
      </Typography>

      <Typography paragraph>
        Maps, estimated distances, and suggested routes may not
        account for vehicle dimensions, weight restrictions,
        hazardous-material restrictions, commercial-vehicle
        limitations, road closures, or current conditions.
        Transportation Providers remain responsible for safe
        and lawful route selection and operation.
      </Typography>

      <Typography paragraph>
        This Disclaimer does not authorize undisclosed
        collection, sharing, or processing of personal
        information. Data handling, including any use of an
        external AI provider, remains subject to the Privacy
        Policy, applicable feature disclosures, and any required
        permissions.
      </Typography>

      <Typography variant="h5" gutterBottom>
        6. Delivery Risks
      </Typography>

      <Typography paragraph>
        Transportation may involve delay, cargo damage, loss,
        theft, contamination, misdelivery, or other risks.
        Responsibility for a particular loss depends on
        applicable law and the valid agreements governing the
        shipment. Use of HaggleFreight does not eliminate a
        claim against a party that is legally responsible.
      </Typography>

      <Typography paragraph>
        A shipment is not necessarily fully performed merely
        because cargo was picked up, payment was captured, or a
        user selected a completed status. Photographs,
        signatures, timestamps, location information, and status
        updates are evidence to consider; they are not
        automatically conclusive proof of performance or a
        waiver of a legitimate claim.
      </Typography>

      <Typography paragraph>
        GoCourier Inc. does not itself provide cargo, vehicle,
        or liability insurance. Using the Platform, declaring a
        cargo value, or uploading an insurance certificate does
        not automatically insure a shipment. Any separately
        obtained coverage is governed by the identified
        insurer's actual policy, exclusions, limits, and
        confirmation of coverage.
      </Typography>

      <Typography paragraph>
        Report loss, damage, delay, and other service problems
        promptly to the appropriate Transportation Provider,
        insurer, or payment provider, and contact HaggleFreight
        for available Platform support. Preserve relevant
        records. A support request does not extend a statutory,
        contractual, insurance, or payment-dispute deadline.
      </Typography>

      <Typography variant="h5" gutterBottom>
        7. User Responsibility
      </Typography>

      <Typography paragraph>
        Customers are responsible for having authority to offer
        the shipment, providing accurate cargo and access
        information, disclosing special requirements, and
        performing the packaging, documentation, loading, and
        other obligations assigned to them by applicable law
        and the accepted transportation agreement.
      </Typography>

      <Typography paragraph>
        Transportation Providers are responsible for
        determining the qualifications, authority, insurance,
        equipment, personnel, and safety requirements applicable
        to their particular operations. Account approval is not
        a substitute for meeting those requirements.
      </Typography>

      <Typography paragraph>
        Users must protect their account credentials, act
        within their authority, submit truthful records, and
        respect other users' privacy and rights. They must not
        require another person to perform unsafe or unlawful
        work. Each party retains the duties that applicable
        law imposes on it.
      </Typography>

      <Typography paragraph>
        These user responsibilities do not transfer
        responsibility for GoCourier Inc.'s own conduct,
        security failures, express commitments, or legal
        obligations to Customers or Transportation Providers.
      </Typography>

      <Typography variant="h5" gutterBottom>
        8. Prohibited Items
      </Typography>

      <Typography paragraph>
        Illegal goods, stolen property, counterfeit goods, and
        shipments prohibited by applicable law are not
        permitted. The Acceptable Use Policy also identifies
        categories that the Platform does not support or that
        are subject to specific restrictions.
      </Typography>

      <Typography paragraph>
        Legal permissibility alone does not override a
        Platform restriction. An item's appearance in a
        listing, a user's willingness to transport it, or an
        informal communication does not establish that the
        shipment is permitted or that required qualifications
        and authorizations have been obtained.
      </Typography>

      <Typography paragraph>
        Disclosing hazardous or special-handling
        characteristics does not, by itself, authorize
        transportation. A prohibited shipment may be removed or
        refused, but a policy violation does not automatically
        authorize forfeiture of all payments. Financial
        consequences remain subject to applicable agreements
        and law.
      </Typography>

      <Typography variant="h5" gutterBottom>
        9. Third-Party Services
      </Typography>

      <Typography paragraph>
        The Platform may depend on third-party hosting,
        communications, payment, mapping, storage, and other
        services. Those providers may have separate terms,
        privacy notices, availability limitations, and
        processing requirements relevant to their services.
      </Typography>

      <Typography paragraph>
        We cannot guarantee that third-party systems,
        websites, communications networks, or financial
        institutions will operate without interruption or
        error. A link or integration is not a guarantee of
        every statement, product, or service offered by that
        third party.
      </Typography>

      <Typography paragraph>
        Reliance on third-party services does not remove
        GoCourier Inc.'s own contractual, privacy, security,
        payment-handling, or other legal obligations. This
        Disclaimer does not exclude responsibility merely
        because a service provider was involved.
      </Typography>

      <Typography variant="h5" gutterBottom>
        10. Payments
      </Typography>

      <Typography paragraph>
        When payment or payout features are enabled, payments
        may be processed through Stripe or another identified
        provider. Users must review the amount, recipient,
        applicable charges, and conditions presented before
        authorizing a transaction.
      </Typography>

      <Typography paragraph>
        A pending payment, estimated payout, authorization
        hold, or displayed balance is not confirmation that
        funds have settled in a bank account. Verification,
        bank processing, lawful payment-provider reviews,
        reversals, and disputes may affect timing. We do not
        guarantee instant settlement or another user's ability
        to pay.
      </Typography>

      <Typography paragraph>
        The Platform is not represented as a bank account,
        insured deposit, escrow service, or guarantee of
        payment. A payment provider's involvement does not by
        itself establish the regulatory status of HaggleFreight
        or remove any legal responsibility applicable to its
        payment activities.
      </Typography>

      <Typography paragraph>
        This Disclaimer does not authorize arbitrary payment
        holds, payout deductions, forfeitures, or additional
        charges. Financial actions must have an applicable
        contractual or legal basis and any required
        authorization. GoCourier Inc. remains responsible for
        accounting for funds, making required refunds, and
        fulfilling other obligations imposed on it.
      </Typography>

      <Typography paragraph>
        The Refund Policy, Cancellation Policy, accepted
        purchase terms, and applicable payment-provider
        procedures govern their respective subjects.
        Contacting HaggleFreight support does not waive a
        good-faith bank, card, app-store, or legal dispute
        right. Support review is not binding arbitration.
      </Typography>

      <Typography variant="h5" gutterBottom>
        11. No Warranty
      </Typography>

      <Typography paragraph>
        To the extent permitted by applicable law, the
        Platform is provided on an "as is" and "as available"
        basis. We do not warrant that it will satisfy every
        user's particular requirements, operate without
        interruption, or be free from every error.
      </Typography>

      <Typography paragraph>
        To the extent permitted by applicable law and
        consistent with the accepted Terms of Service, we
        disclaim implied warranties of merchantability,
        fitness for a particular purpose, and noninfringement
        concerning the Platform. These exclusions apply only
        where and to the extent they may lawfully apply.
      </Typography>

      <Typography paragraph>
        Nothing in this section overrides an express
        commitment we make, an applicable consumer guarantee,
        a required duty of care, or a warranty or protection
        that cannot lawfully be excluded. It does not reduce
        nonwaivable rights under an applicable app-software
        license or mandatory app-store terms.
      </Typography>

      <Typography paragraph>
        No statement here excuses misleading descriptions of
        our services, inadequate performance of an obligation
        imposed by law, or failure to honor an applicable
        accepted purchase commitment.
      </Typography>

      <Typography variant="h5" gutterBottom>
        12. Limitation of Liability
      </Typography>

      <Typography paragraph>
        Subject to the Terms of Service and to the maximum
        extent permitted by applicable law, GoCourier Inc. and
        its officers, employees, and service providers are not
        liable for indirect, incidental, special,
        consequential, exemplary, or punitive damages arising
        from use of the Platform, including indirect lost
        profits, lost business opportunities, or loss of
        goodwill.
      </Typography>

      <Typography paragraph>
        Whether a loss is direct, indirect, or otherwise
        recoverable depends on applicable law and the facts.
        Cargo loss, shipment damage, payment errors, and
        service failures are not automatically excluded
        merely because they occurred in connection with a
        Platform transaction.
      </Typography>

      <Typography paragraph>
        Nothing in this Disclaimer excludes or limits
        liability for our fraud, willful misconduct, gross
        negligence, or any liability that cannot lawfully be
        excluded or limited. These limitations do not erase
        obligations to account for funds, provide required
        refunds, or pay money lawfully owed.
      </Typography>

      <Typography paragraph>
        The liability provisions of the accepted Terms of
        Service govern their scope and application. This
        Disclaimer does not create a separate monetary cap,
        shorten a legal claim deadline, or expand an exclusion
        beyond what the applicable agreement and law permit.
      </Typography>

      <Typography variant="h5" gutterBottom>
        13. Use at Your Own Risk
      </Typography>

      <Typography paragraph>
        Users should evaluate the ordinary risks of online
        marketplace activity and transportation and make
        informed decisions about shipments, counterparties,
        prices, insurance, and the disclosure of personal
        information.
      </Typography>

      <Typography paragraph>
        This statement is not a blanket release of claims or
        an agreement to assume risks caused by conduct for
        which another party remains legally responsible.
        Electronic acceptance of Platform agreements does not
        waive rights that applicable law does not allow users
        to waive.
      </Typography>

      <Typography paragraph>
        General information, support communications, and
        automated outputs should not be treated as
        individualized legal, tax, insurance, or regulatory
        advice. Required transportation documents, applicable
        laws, insurer requirements, and the parties' actual
        agreements remain relevant.
      </Typography>

      <Typography paragraph>
        HaggleFreight is not an emergency-response service.
        For an immediate threat to life, safety, or property,
        contact the appropriate emergency authorities. Do not
        use the Platform in a manner that distracts from safe
        vehicle operation or delays necessary emergency
        action.
      </Typography>

      <Typography paragraph>
        This Disclaimer does not replace the account-deletion
        process, privacy choices, reporting tools, or other
        controls described in the applicable policies. It
        does not independently impose arbitration, a
        class-action waiver, or a new agreement to a particular
        court or jurisdiction.
      </Typography>

      <Typography variant="h5" gutterBottom>
        14. Contact
      </Typography>

      <Typography paragraph>
        Questions about this Disclaimer, Platform support,
        or the applicable agreements may be directed to
        GoCourier Inc., operator of HaggleFreight, at
        support@hagglefreight.com.
      </Typography>

      <Typography paragraph>
        When reporting a transaction issue, include your
        account email, the relevant shipment or transaction
        identifier, and a clear explanation. Do not send
        passwords, payment-card security codes, complete
        payment-card numbers, or full bank-account
        credentials through email or ordinary messages.
      </Typography>

      <Typography paragraph>
        Changes to this Disclaimer are handled under the
        notice and acceptance provisions of the Terms of
        Service. Changes do not retroactively remove accrued
        rights or authorize new charges. The date above should
        identify the version currently published on the
        Platform.
      </Typography>
    </LegalPageLayout>
  );
};

export default Disclaimer;